import * as SpeechSDK from "microsoft-cognitiveservices-speech-sdk";
import reduxStore from "../configureEnvStore";
import {
    setTranslationState,
    setTranslationEnabled,
    setTranslationError,
    setCurrentTranslatedText,
    setCurrentOriginalText,
    setTranslatedTextVisible,
    setParticipantTranslationStatus,
    removeParticipantTranslationStatus,
} from "../reducers/audioTranslation-reducer";
import { AUDIO_CONFIG, TRANSLATION_STATE, AZURE_TTS_VOICES, VOICE_TYPES } from "../constants/AudioTranslationConstants";
import { LogFirebase } from "./firestore";
import { getRoom } from "../components/Twilio/Room/roomService";
import { getTracksByType } from "../components/Twilio/twilioCallUtility";

class AudioTranslationService {
    constructor() {
        // Per-participant recognizer state
        // Map<identity, { recognizer, captureContext, pushStream, scriptNode, sourceNode }>
        this.participantSessions = new Map();

        // Shared audio context for translated playback
        this.playbackAudioContext = null;

        // Audio playback queue — schedules chunks sequentially
        this.audioQueue = [];
        this.nextPlayTime = 0;

        // Text visibility timer
        this.textVisibilityTimer = null;
    }

    // ---- PUBLIC API ----

    /**
     * Start translation for all currently connected remote participants.
     */
    startTranslation() {
        const state = reduxStore.getState().audioTranslation;
        const { sourceLanguage, targetLanguageCode, voiceType } = state;

        if (!sourceLanguage || !targetLanguageCode) {
            LogFirebase("AudioTranslation", "start_failed", "missing_languages");
            return;
        }

        reduxStore.dispatch(setTranslationState(TRANSLATION_STATE.STARTING));
        LogFirebase("AudioTranslation", "starting", `source:${sourceLanguage}_target:${targetLanguageCode}_voice:${voiceType}`);

        try {
            const room = getRoom();
            if (!room?.participants) {
                throw new Error("No active room or participants");
            }

            // Create shared playback context
            this.playbackAudioContext = new (window.AudioContext || window.webkitAudioContext)();

            // Start translation for each remote participant with audio tracks
            room.participants.forEach((remoteParticipant) => {
                const audioTracks = getTracksByType(remoteParticipant, "audio");
                if (audioTracks.length > 0 && audioTracks[0].isEnabled) {
                    this.startParticipantTranslation(remoteParticipant.identity, audioTracks[0], sourceLanguage, targetLanguageCode, voiceType);
                }
            });

            reduxStore.dispatch(setTranslationState(TRANSLATION_STATE.ACTIVE));
            reduxStore.dispatch(setTranslationEnabled(true));
            LogFirebase("AudioTranslation", "started_successfully", "true");
        } catch (error) {
            console.error("AudioTranslation: startTranslation failed", error);
            reduxStore.dispatch(setTranslationError({ message: error?.message || "Failed to start translation" }));
            LogFirebase("AudioTranslation", "start_error", error?.message);
        }
    }

    /**
     * Stop all active translations.
     */
    stopTranslation() {
        reduxStore.dispatch(setTranslationState(TRANSLATION_STATE.STOPPING));
        LogFirebase("AudioTranslation", "stopping", "true");

        // Stop all participant sessions
        this.participantSessions.forEach((session, identity) => {
            this.stopParticipantTranslation(identity);
        });

        // Clear audio playback queue
        this.audioQueue = [];
        this.nextPlayTime = 0;

        // Close shared playback context
        if (this.playbackAudioContext) {
            this.playbackAudioContext.close().catch(() => {});
            this.playbackAudioContext = null;
        }

        // Restore original audio volumes
        this.restoreAllOriginalAudioVolumes();

        // Clear text display
        clearTimeout(this.textVisibilityTimer);
        reduxStore.dispatch(setTranslatedTextVisible(false));
        reduxStore.dispatch(setCurrentTranslatedText(""));
        reduxStore.dispatch(setCurrentOriginalText(""));

        reduxStore.dispatch(setTranslationState(TRANSLATION_STATE.IDLE));
        reduxStore.dispatch(setTranslationEnabled(false));
        LogFirebase("AudioTranslation", "stopped", "true");
    }

    /**
     * Called when a new remote audio track is subscribed.
     * If translation is active, automatically starts translating for the new participant.
     */
    onRemoteAudioTrackSubscribed(track, participantIdentity) {
        const state = reduxStore.getState().audioTranslation;
        if (state.translationEnabled && state.translationState === TRANSLATION_STATE.ACTIVE) {
            const { sourceLanguage, targetLanguageCode, voiceType } = state;
            this.startParticipantTranslation(participantIdentity, track, sourceLanguage, targetLanguageCode, voiceType);
            LogFirebase("AudioTranslation", "new_participant_added", participantIdentity);
        }
    }

    /**
     * Called when a remote participant disconnects.
     */
    onRemoteParticipantDisconnected(participantIdentity) {
        if (this.participantSessions.has(participantIdentity)) {
            this.stopParticipantTranslation(participantIdentity);
        }
    }

    /**
     * Full cleanup on call end.
     */
    resetTranslation() {
        this.stopTranslation();
        this.participantSessions.clear();
        clearTimeout(this.textVisibilityTimer);
    }

    // ---- PRIVATE: Per-Participant Translation ----

    startParticipantTranslation(identity, twilioAudioTrack, sourceLanguage, targetLanguageCode, voiceType = VOICE_TYPES.FEMALE) {
        // Prevent duplicate sessions
        if (this.participantSessions.has(identity)) {
            this.stopParticipantTranslation(identity);
        }

        try {
            // 1. Create Azure SpeechTranslationConfig
            const speechConfig = SpeechSDK.SpeechTranslationConfig.fromSubscription(
                process.env.REACT_APP_LIVECAPTION_SUBSCRIPTIONKEY,
                process.env.REACT_APP_LIVECAPTION_REGION
            );
            speechConfig.speechRecognitionLanguage = sourceLanguage;
            speechConfig.addTargetLanguage(targetLanguageCode);

            // Enable voice synthesis for the target language with selected voice type
            const voiceEntry = AZURE_TTS_VOICES[targetLanguageCode];
            if (voiceEntry) {
                speechConfig.voiceName = voiceEntry[voiceType] || voiceEntry[VOICE_TYPES.FEMALE];
            }

            speechConfig.setProperty(SpeechSDK.PropertyId.SpeechServiceConnection_SynthOutputFormat, "audio-16khz-32kbitrate-mono-mp3");

            // 2. Create PushAudioInputStream for feeding PCM data
            const pushStream = SpeechSDK.AudioInputStream.createPushStream(
                SpeechSDK.AudioStreamFormat.getWaveFormatPCM(AUDIO_CONFIG.SAMPLE_RATE, AUDIO_CONFIG.BITS_PER_SAMPLE, AUDIO_CONFIG.CHANNELS)
            );
            const audioConfig = SpeechSDK.AudioConfig.fromStreamInput(pushStream);

            // 3. Create TranslationRecognizer
            const recognizer = new SpeechSDK.TranslationRecognizer(speechConfig, audioConfig);

            // 4. Set up Web Audio API pipeline to capture Twilio remote audio
            const captureContext = new (window.AudioContext || window.webkitAudioContext)({
                sampleRate: AUDIO_CONFIG.SAMPLE_RATE,
            });

            const mediaStream = new MediaStream([twilioAudioTrack.mediaStreamTrack]);
            const sourceNode = captureContext.createMediaStreamSource(mediaStream);

            // ScriptProcessorNode to extract PCM samples
            const scriptNode = captureContext.createScriptProcessor(AUDIO_CONFIG.BUFFER_SIZE, AUDIO_CONFIG.CHANNELS, AUDIO_CONFIG.CHANNELS);

            // Connect: source -> scriptNode -> destination (destination needed for onaudioprocess to fire)
            sourceNode.connect(scriptNode);
            scriptNode.connect(captureContext.destination);

            // Process audio data and push to Azure
            scriptNode.onaudioprocess = (audioProcessingEvent) => {
                const inputData = audioProcessingEvent.inputBuffer.getChannelData(0);
                const pcmData = this.float32ToInt16(inputData);
                try {
                    pushStream.write(pcmData.buffer);
                } catch (err) {
                    // Non-fatal: next buffer will succeed
                    console.warn("AudioTranslation: pushStream.write error", err);
                }
            };

            // 5. Lower the volume of the original <audio> element
            this.lowerOriginalAudioVolume();

            // 6. Wire up recognizer events
            recognizer.recognizing = (sender, event) => {
                const translation = event.result.translations?.get(targetLanguageCode);
                if (translation) {
                    reduxStore.dispatch(setCurrentOriginalText(event.result.text));
                    reduxStore.dispatch(setCurrentTranslatedText(translation));
                    this.showTranslatedText();
                }
            };

            recognizer.recognized = (sender, event) => {
                if (event.result.reason === SpeechSDK.ResultReason.TranslatedSpeech) {
                    const translation = event.result.translations?.get(targetLanguageCode);
                    if (translation) {
                        reduxStore.dispatch(setCurrentOriginalText(event.result.text));
                        reduxStore.dispatch(setCurrentTranslatedText(translation));
                        this.showTranslatedText();
                    }
                }
            };

            // Handle synthesized audio for playback
            recognizer.synthesizing = (sender, event) => {
                if (event.result.audio && event.result.audio.byteLength > 0) {
                    this.playTranslatedAudio(event.result.audio);
                }
            };

            recognizer.canceled = (sender, event) => {
                console.error("AudioTranslation: recognizer canceled", event.errorDetails);
                LogFirebase("AudioTranslation", "recognizer_canceled", event.errorDetails);
                if (event.reason === SpeechSDK.CancellationReason.Error) {
                    reduxStore.dispatch(setTranslationError({ message: event.errorDetails }));
                }
            };

            recognizer.sessionStopped = () => {
                LogFirebase("AudioTranslation", "session_stopped", identity);
            };

            // 7. Start continuous recognition
            recognizer.startContinuousRecognitionAsync(
                () => {
                    LogFirebase("AudioTranslation", "recognizer_started", identity);
                    reduxStore.dispatch(
                        setParticipantTranslationStatus({
                            identity,
                            status: { active: true, sourceLanguage },
                        })
                    );
                },
                (error) => {
                    console.error("AudioTranslation: startContinuousRecognitionAsync failed", error);
                    LogFirebase("AudioTranslation", "recognizer_start_error", error);
                    reduxStore.dispatch(setTranslationError({ message: String(error) }));
                }
            );

            // 8. Store session state
            this.participantSessions.set(identity, {
                recognizer,
                captureContext,
                pushStream,
                scriptNode,
                sourceNode,
            });
        } catch (error) {
            console.error(`AudioTranslation: failed to start for ${identity}`, error);
            LogFirebase("AudioTranslation", "participant_start_error", error?.message);
        }
    }

    stopParticipantTranslation(identity) {
        const session = this.participantSessions.get(identity);
        if (!session) return;

        try {
            // Stop recognizer
            session.recognizer.stopContinuousRecognitionAsync(
                () => LogFirebase("AudioTranslation", "recognizer_stopped", identity),
                (err) => console.error("AudioTranslation: stop error", err)
            );

            // Close push stream
            session.pushStream.close();

            // Disconnect and close Web Audio nodes
            session.scriptNode.disconnect();
            session.sourceNode.disconnect();
            session.captureContext.close().catch(() => {});

            // Close recognizer
            session.recognizer.close();

            // Remove from map
            this.participantSessions.delete(identity);

            // Update Redux
            reduxStore.dispatch(removeParticipantTranslationStatus(identity));
        } catch (error) {
            console.warn(`AudioTranslation: cleanup error for ${identity}`, error);
            this.participantSessions.delete(identity);
        }
    }

    // ---- PRIVATE: Audio Helpers ----

    /**
     * Convert Float32Array audio samples to Int16 PCM buffer.
     * Azure Speech SDK PushAudioInputStream expects 16-bit PCM.
     */
    float32ToInt16(float32Array) {
        const int16Array = new Int16Array(float32Array.length);
        for (let i = 0; i < float32Array.length; i++) {
            const s = Math.max(-1, Math.min(1, float32Array[i]));
            int16Array[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
        }
        return int16Array;
    }

    /**
     * Play synthesized translated audio using Web Audio API.
     * Queues audio chunks and plays them sequentially to maintain speaking order.
     */
    playTranslatedAudio(audioData) {
        if (!this.playbackAudioContext || this.playbackAudioContext.state === "closed") return;

        // Resume if suspended (Chrome autoplay policy)
        if (this.playbackAudioContext.state === "suspended") {
            this.playbackAudioContext.resume();
        }

        // Add to queue and process
        this.audioQueue.push(audioData.slice(0)); // slice to avoid detached buffer
        this.processAudioQueue();
    }

    /**
     * Process the audio queue — decode and schedule all pending chunks immediately.
     * Uses AudioContext time-scheduling to play them back-to-back without gaps.
     */
    processAudioQueue() {
        if (!this.playbackAudioContext || this.playbackAudioContext.state === "closed") {
            this.audioQueue = [];
            return;
        }

        while (this.audioQueue.length > 0) {
            const audioData = this.audioQueue.shift();
            this.decodeAndSchedule(audioData);
        }
    }

    decodeAndSchedule(audioData) {
        this.playbackAudioContext.decodeAudioData(
            audioData,
            (audioBuffer) => {
                const source = this.playbackAudioContext.createBufferSource();
                source.buffer = audioBuffer;
                source.connect(this.playbackAudioContext.destination);

                // Schedule playback: either now or after the previous chunk ends
                const currentTime = this.playbackAudioContext.currentTime;
                const startTime = Math.max(currentTime, this.nextPlayTime);
                source.start(startTime);

                // Track when this chunk will finish so next chunk starts after it
                this.nextPlayTime = startTime + audioBuffer.duration;
            },
            (error) => {
                console.warn("AudioTranslation: decodeAudioData failed", error);
            }
        );
    }

    /**
     * Lower the volume of all original Twilio <audio> elements.
     */
    lowerOriginalAudioVolume() {
        const audioElements = document.querySelectorAll("#audioTag audio");
        audioElements.forEach((audioEl) => {
            audioEl.volume = AUDIO_CONFIG.ORIGINAL_VOLUME_WHEN_TRANSLATING;
        });
    }

    restoreAllOriginalAudioVolumes() {
        const audioElements = document.querySelectorAll("#audioTag audio");
        audioElements.forEach((audioEl) => {
            audioEl.volume = 1.0;
        });
    }

    // ---- PRIVATE: Text Display Timer ----

    showTranslatedText() {
        reduxStore.dispatch(setTranslatedTextVisible(true));
        clearTimeout(this.textVisibilityTimer);
        this.textVisibilityTimer = setTimeout(() => {
            reduxStore.dispatch(setTranslatedTextVisible(false));
        }, 8000);
    }
}

export default new AudioTranslationService();
